<script setup lang="ts">
/**
 * FileDropOverlay — the shared "you're dragging a file over a drop target" overlay.
 * Purely presentational: the parent owns the drag detection and the drop action
 * (add to chat context, …); this just renders the highlight — a tinted dashed panel
 * filling its positioned parent.
 * It is `pointer-events-none` so drag/drop events still reach the parent handler.
 * (The Sheets page uses a plain inset ring instead — a scroll container can't host an
 * absolute overlay cleanly — so it intentionally does not use this component.)
 *
 * A second `backdrop` variant used to live here: a dimmed, blurred full-surface veil for the
 * app-wide global-upload overlay in `PanelLayout`. That overlay went with the global drop zone
 * (#83) and the variant with it — there is no whole-window drop to announce any more.
 */
defineProps<{
  active: boolean
  label: string
  hint?: string
}>()
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-150" enter-from-class="opacity-0"
    leave-active-class="transition-opacity duration-150" leave-to-class="opacity-0"
  >
    <div
      v-if="active"
      class="pointer-events-none absolute inset-0 z-50 flex items-center justify-center
             rounded-lg border-2 border-dashed border-primary bg-primary-lighter"
    >
      <div class="flex flex-col items-center gap-2 text-center">
        <slot name="icon" />
        <p class="text-sm font-medium text-primary">{{ label }}</p>
        <p v-if="hint" class="text-xs text-muted-foreground">{{ hint }}</p>
      </div>
    </div>
  </Transition>
</template>
